import { useMemo } from 'react';

import { Mail, Phone, MessageSquare, Ticket, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { useDynamicWhatsapp } from 'hooks/useDynamicWhatsapp';
import { toast } from 'lib/toast-unified';

// Mock ticket data
const mockTicket = {
  id: '12345',
  subject: 'Dúvida sobre integração',
  createdAt: '2024-01-20',
};

const SupportTab = () => {
  const { t, i18n } = useTranslation('common');
  const { whatsappNumber, whatsappText, isLoading: isWhatsappLoading } = useDynamicWhatsapp();

  const faqData = useMemo(
    () => [
      { id: 'password', question: t('support.faqPassword'), answer: t('support.faqPasswordAnswer') },
      { id: 'contact', question: t('support.faqContact'), answer: t('support.faqContactAnswer') },
      { id: 'cancel-meeting', question: t('support.faqCancelMeeting'), answer: t('support.faqCancelMeetingAnswer') },
      { id: 'access-issues', question: t('support.faqAccessIssues'), answer: t('support.faqAccessIssuesAnswer') },
    ],
    [t]
  );

  const handleOpenTicket = () => {
    toast.info(t('support.inDevelopment'), {
      description: t('support.inDevelopmentDescription'),
    });
  };

  const handleEmailContact = () => {
    window.location.href = 'mailto:suporte@algoricca.com';
  };

  const handleWhatsAppContact = () => {
    if (!whatsappNumber) {
      toast.error(t('support.whatsappUnavailable'), {
        description: t('support.whatsappNotConfigured'),
      });
      return;
    }

    // Remove formatting to get raw number for WhatsApp URL
    const rawNumber = whatsappNumber.replace(/\D/g, '');
    const message = encodeURIComponent(whatsappText || t('support.whatsappDefaultMessage'));
    window.open(`https://wa.me/${rawNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">{t('support.title')}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('support.subtitle')}
        </p>
      </div>

      {/* FAQ Accordion */}
      <div>
        <h4 className="text-sm font-semibold mb-3">{t('support.faq')}</h4>
        <Accordion type="single" collapsible className="w-full">
          {faqData.map(faq => (
            <AccordionItem key={faq.id} value={faq.id}>
              <AccordionTrigger className="text-sm">{faq.question}</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground">{faq.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Contact Options */}
      <div>
        <h4 className="text-sm font-semibold mb-3">{t('support.contactUs')}</h4>
        <div className="space-y-2">
          <Button variant="outline" className="w-full justify-start" onClick={handleEmailContact}>
            <Mail className="h-4 w-4 mr-2" />
            {t('support.emailLabel')}
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleWhatsAppContact}
            disabled={isWhatsappLoading || !whatsappNumber}
          >
            {isWhatsappLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Phone className="h-4 w-4 mr-2" />
            )}
            {isWhatsappLoading
              ? t('actions.loading')
              : whatsappNumber
                ? `WhatsApp: ${whatsappNumber}`
                : t('support.whatsappUnavailable')}
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={handleOpenTicket}>
            <MessageSquare className="h-4 w-4 mr-2" />
            {t('support.openTicket')}
          </Button>
        </div>
      </div>

      {/* Ticket Status Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold">{t('support.myTickets')}</h4>
          <Badge variant="warning" className="text-xs">
            {t('support.simulatedData')}
          </Badge>
        </div>
        <div className="border rounded-lg p-3 space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2">
              <Ticket className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Ticket #{mockTicket.id}</p>
                <p className="text-xs text-muted-foreground">{mockTicket.subject}</p>
              </div>
            </div>
            <Badge variant="info" className="text-xs">
              {t('support.inProgress')}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground pl-6">
            {t('support.openedAt')} {new Date(mockTicket.createdAt).toLocaleDateString(i18n.language)}
          </p>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {t('support.ticketsInDevelopment')}
        </p>
      </div>
    </div>
  );
};

export default SupportTab;
